import { useEffect, useRef, useState } from "react";
import { useAsyncAction } from "./useAsyncAction";

/** Preserve caller arguments and identify the active row while duplicate calls are locked. */
export function useAsyncCallback<Arguments extends unknown[]>(
  action: (...args: Arguments) => Promise<void>,
) {
  const { pending, run } = useAsyncAction();
  const [activeArguments, setActiveArguments] = useState<Arguments | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const callback = (...args: Arguments) => {
    // Every form event must be prevented, including duplicate submissions skipped by the lock.
    const event = args[0];
    if (
      event && typeof event === "object" && "type" in event && event.type === "submit" &&
      "preventDefault" in event && typeof event.preventDefault === "function"
    ) {
      event.preventDefault();
    }
    return run(async () => {
      if (mounted.current) setActiveArguments(args);
      try {
        await action(...args);
      } finally {
        if (mounted.current) setActiveArguments(null);
      }
    });
  };
  return { pending, activeArguments, callback };
}
